import { giraffeState, rpc } from "@gi-nx/iframe-sdk";
import { asHostToken, type HostAdapter } from "./adapter";

/**
 * The real host. **THE ONLY MODULE IN THIS REPO THAT IMPORTS `@gi-nx/*`** —
 * premise P1 in executable form, enforced by Biome BLOCK 2 and by
 * `.dependency-cruiser.cjs` `only-host-reaches-the-sdk`.
 *
 * ## Why this is its own file rather than the bottom of `adapter.ts`
 *
 * `@gi-nx/iframe-sdk` reads `window` at MODULE LOAD (`mini-iframe-rpc.ts:63`),
 * so importing it in a bare Node process throws `ReferenceError: window is not
 * defined` before a single line of our code runs. Putting it beside
 * `fakeHostAdapter` would make the fake unimportable in exactly the environment
 * the fake exists for. The seam's testability is the whole reason `host/`
 * exists; a file boundary is a cheap price for keeping it.
 *
 * Until this module existed the dependency-cruiser rule was VACUOUS: the
 * package was not installed, so its `to` side matched nothing in the graph and
 * the stage passed by having nothing to say. See `verify:selftest`'s
 * `dep:check/only-host-reaches-the-sdk-is-not-vacuous`.
 */
export function realHostAdapter(): HostAdapter {
  const invoke = async (fn: string, args: readonly unknown[]): Promise<unknown> =>
    await rpc.invoke(fn as never, args as never);

  return {
    invoke,
    getState: (key) => {
      const value: unknown = giraffeState.get(key as never);
      // ⚠️ `!== undefined`, NOT truthiness: the geo-typed keys return a
      // well-formed EMPTY FeatureCollection for a key that never arrived, so
      // "not pushed yet" and "genuinely empty" would otherwise collapse into
      // one answer — and they are different answers.
      return { present: value !== undefined, value: value ?? null };
    },
    userToken: async () => asHostToken(await invoke("getUserClaimsJwt", [])),
    onStateChange: (keys, listener) => {
      const handle = giraffeState.addListener(keys as never, (key) => {
        listener(String(key));
      });
      return () => {
        giraffeState.removeListener(handle);
      };
    },
  };
}
